const unorm = require('unorm');

function extendInlineParserToInjectTokenMarker(md) {
  const ruler = md.inline.ruler;
  // eslint-disable-next-line no-underscore-dangle
  ruler.__rules__.forEach((rule) => {
    if (!rule.enabled) {
      return;
    }

    const oldFn = rule.fn;
    ruler.at(rule.name, function (state, ...args) {
      const oldPos = state.pos;
      const oldTokens = state.tokens.slice();

      const result = oldFn(state, ...args);
      if (!result) {
        return result;
      }

      if (oldTokens.length < state.tokens.length) {
        const increasedTokens = state.tokens.slice(oldTokens.length - state.tokens.length);
        const increasedTagTokens = increasedTokens.filter((token) => !!token.tag);
        if (increasedTagTokens.length > 0) {
          const targetToken = increasedTagTokens[0];
          const newPos = state.pos;
          targetToken.attrPush(['data-md-inline', `${Number(oldPos).toString(36)},${Number(newPos - 1).toString(36)}`]);
        }
      }

      return result;
    }, { alt: rule.alt });
  });
}

module.exports = function markdownItFactory(withTokenMarker, withSupportWebP, imageSizes, tooltips) {
  const md = require('markdown-it')({
    breaks: true,
    // NOTE: popup-contentの要素数がmaxNestingを超えるとpopup-content-wrapperが生成されない (https://app.asana.com/0/1206747620821156/1207572629287976/f)
    maxNesting: 300,
  })
    .use(require('./markdown-it-plugins/markdown-it-header-sections.js'))
    .use(require('markdown-it-header-sections'))
    .use(require('./markdown-it-plugins/markdown-it-header-remover.js'))
    .use(require('./markdown-it-plugins/markdown-it-br-tag.js'))
    .use(require('markdown-it-deflist'))
    .use(require('./markdown-it-plugins/markdown-it-hidden-div.js'))
    .use(require('./markdown-it-plugins/markdown-it-span.js'))
    .use(require('./markdown-it-plugins/markdown-it-remove-empty-table-header.js'))
    .use(require('./markdown-it-plugins/markdown-it-table-rowspan.js'))
    .use(require('./markdown-it-plugins/markdown-it-table-colspan.js'))
    .use(require('./markdown-it-plugins/markdown-it-td-class.js'))
    .use(require('./markdown-it-plugins/markdown-it-block-class.js'))
    .use(require('./markdown-it-plugins/markdown-it-url-embedded.js'))
    .use(require('./markdown-it-plugins/markdown-it-keyword-autolink.js'))
    .use(require('./markdown-it-plugins/markdown-it-command.js'))
    .use(require('./markdown-it-plugins/markdown-it-a-nofollow'))
    .use(require('./markdown-it-plugins/markdown-it-data-embed.js'))
    // NOTE: wiki プレフィックスがついてしまってるパスに変換し、このパスにクローラーを呼び込むための一時的な処理です。したがって、この対応が終了したらこの処理は削除します。
    // REF: https://app.asana.com/1/941909390465892/project/1202642188581172/task/1209933364659406?focus=true
    .use(require('./markdown-it-plugins/markdown-it-add-wiki-prefix.js'))
    .use(require('./markdown-it-plugins/markdown-it-a-target-blank.js'))
    .use(require('./markdown-it-plugins/markdown-it-custom-format-comment.js'))
    .use(require('./markdown-it-plugins/markdown-it-custom-calculator.js'))
    .use(require('./markdown-it-plugins/markdown-it-append-helper-scripts.js'))
    .use(require('./markdown-it-plugins/markdown-it-bingo-table.js'))
    .use(require('./markdown-it-plugins/markdown-it-switch-content-div.js'))
    .use(require('./markdown-it-plugins/markdown-it-popup-content-div.js'))
    .use(require('./markdown-it-plugins/markdown-it-chart.js'))
    .use(require('./markdown-it-plugins/markdown-it-custom-html-component.js'))
    .use(require('./markdown-it-plugins/markdown-it-mp4-to-video.js'));
  if (withTokenMarker) {
    md.use(require('./markdown-it-plugins/markdown-it-comment-block.js'));
    md.use(require('./markdown-it-plugins/markdown-it-comment-inline.js'));
    md.use(require('./markdown-it-plugins/inject-token-marker.js'));
  } else {
    md.use(require('./markdown-it-plugins/markdown-it-erase-comment.js'));
  }
  if (withSupportWebP) {
    md.use(require('./markdown-it-plugins/markdown-it-img-to-picture.js')(imageSizes));
  }

  if (Object.keys(tooltips).length > 0) {
    md.use(require('./markdown-it-plugins/markdown-it-tooltip.js')(tooltips));
  }

  const Token = require('markdown-it/lib/token');

  md.normalizeLink = md.utils.lib.mdurl.encode;
  md.normalizeLinkText = (str) => str;

  if (withTokenMarker) {
    extendInlineParserToInjectTokenMarker(md);
  }

  const parse = function (element, result, sectionStack = []) {
    if (element.tag !== 'section') {
      result.push(element);

      return;
    }

    if (element.type === 'section_close') {
      sectionStack.pop();
      return;
    }

    const attrs = new Map(element.attrs);

    sectionStack.push(attrs.get('data-section-id'));

    const text = new Token('text', '', 0);
    text.content = attrs.get('data-section-id');

    const headerOpen = new Token('heading_open', 'h' + attrs.get('data-level'), 1);
    headerOpen.attrs = [['id', sectionStack.join('--')]];
    if (attrs.get('class')) {
      headerOpen.attrs.push(['class', attrs.get('class')]);
    }
    if (attrs.get('data-md-block') && withTokenMarker) {
      headerOpen.attrs.push(['data-md-block', attrs.get('data-md-block')]);
      headerOpen.attrs.push(['data-md-inline', `0,${Number(text.content.length - 1).toString(36)}`]);
    }
    result.push(headerOpen);

    const headerContent = new Token('inline', '', 0);
    headerContent.content = attrs.get('data-section-id');
    headerContent.children = [text];
    result.push(headerContent);

    const headerClose = new Token('heading_close', 'h' + attrs.get('data-level'), -1);
    result.push(headerClose);
  };

  md.dump = function (parsed, sectionStack = []) {
    const result = [];
    parsed.forEach((element) => {
      parse(element, result, sectionStack);
    });

    return result;
  };

  md.load = function (input, game, autolink, referrerUrl) {
    const env = { game, autolink, referrerUrl };
    if (!input) {
      return [];
    }
    const parsed = md.parse(unorm.nfc(input.replace(/\r\n/g, '\n').replace(/\uFEFF/g, '')), env);

    return parsed;
  };

  md.applyLinkToHtmlToTextNode = function ($, content, title, filepath, autolink) {
    $('p, li, td', content).contents().each(function (index, element) {
      if (element.type !== 'text') {
        return;
      }

      $(element).replaceWith(autolink.addLinkToHtml(autolink.game, $(element).text(), title, filepath));
    });
  };

  md.renderEntireSections = function (parsed, game, title, filepath, autolink, baseLevel = 1) {
    const env = { game };
    const filtered = md.dump(parsed);
    const result = md.renderer.render(filtered, md.options, env);

    return result;
  };

  md.helperScripts = new Set();

  return md;
};
